import { Edge, Node } from 'reactflow';
import ELK, { ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk.bundled.js';

import { EdgeData, NodeData, PolicyGraph } from '../types/graph';

const elk = new ELK();

// Elk does not have a proper type for its layout options, these values are coming from the following docs
// - https://www.eclipse.org/elk/reference/options.html
type ElkLayoutOptions = {
    'elk.algorithm'?: string;
    'elk.layered.spacing.nodeNodeBetweenLayers'?: string;
    'elk.layered.spacing.edgeNodeBetweenLayers'?: string;
    'elk.spacing.nodeNode'?: string;
    'elk.spacing.edgeNode'?: string;
    'elk.direction'?: 'RIGHT' | 'LEFT' | 'DOWN' | 'UP';
    'elk.layered.priority.direction'?: string;
    'elk.layered.considerModelOrder.strategy'?: 'NODES_AND_EDGES' | 'PREFER_EDGES' | 'PREFER_NODES';
    'elk.edge.type'?: 'DIRECTED' | 'UNDIRECTED' | 'ASSOCIATION' | 'GENERALIZATION' | 'DEPENDENCY';
    'elk.layered.nodePlacement.bk.fixedAlignment'?: 'LEFTUP' | 'RIGHTUP' | 'LEFTDOWN' | 'RIGHTDOWN' | 'BALANCED';
};

const ELK_DEFAULT_CONFIG: ElkLayoutOptions = {
    'elk.algorithm': 'layered',
    'elk.edge.type': 'DIRECTED',
    'elk.spacing.nodeNode': '50',
    'elk.spacing.edgeNode': '50',
    'elk.layered.considerModelOrder.strategy': 'PREFER_NODES',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
};

export type LayoutOption = 'vertical' | 'horizontal';

const ELK_VERTICAL_CONFIG: ElkLayoutOptions = {
    'elk.direction': 'DOWN',
    'elk.layered.spacing.nodeNodeBetweenLayers': '150',
};

const ELK_HORIZONTAL_CONFIG: ElkLayoutOptions = {
    'elk.direction': 'RIGHT',
    'elk.layered.spacing.nodeNodeBetweenLayers': '400',
};

const ELKS_CONFIGS: Record<LayoutOption, ElkLayoutOptions> = {
    vertical: ELK_VERTICAL_CONFIG,
    horizontal: ELK_HORIZONTAL_CONFIG,
};

const _processGroupNodes = (nodes: Node<NodeData>[]): Node<NodeData>[] => {
    try {
        const groupNodes = nodes.filter((node) => node.type === 'policyGroup');

        if (groupNodes.length === 0) {
            return nodes;
        }

        groupNodes.forEach((groupNode) => {
            try {
                const groupStyle = groupNode.data.style;
                if (!groupStyle) {
                    console.warn(`Group node ${groupNode.id} has no style`);
                    return;
                }

                const childNodes = nodes.filter((node) => node.parentId === groupNode.id);

                if (childNodes.length === 0) {
                    return;
                }

                const childAbsolutePositions = childNodes.map((child) => {
                    const absX = child.position.x;
                    const absY = child.position.y;
                    return {
                        node: child,
                        absX,
                        absY,
                        width: child.width || 300,
                        height: child.height || 200,
                    };
                });

                const minX = Math.min(...childAbsolutePositions.map((pos) => pos.absX));
                const minY = Math.min(...childAbsolutePositions.map((pos) => pos.absY));
                const maxX = Math.max(...childAbsolutePositions.map((pos) => pos.absX + pos.width));
                const maxY = Math.max(...childAbsolutePositions.map((pos) => pos.absY + pos.height));

                groupNode.position.x = minX;
                groupNode.position.y = minY;

                childNodes.forEach((child) => {
                    const absPos = childAbsolutePositions.find((pos) => pos.node.id === child.id);
                    if (absPos) {
                        child.position.x = absPos.absX - minX;
                        child.position.y = absPos.absY - minY;
                    }
                });

                if (groupNode.data.style) {
                    groupNode.data.style.width = maxX - minX;
                    groupNode.data.style.height = maxY - minY;
                }
            } catch (groupError) {
                console.error(`Error processing group node ${groupNode.id}:`, groupError);
            }
        });

        return nodes;
    } catch (error) {
        console.error('Error in processGroupNodes:', error);
        return nodes;
    }
};

const layoutGraph = async (
    graph: PolicyGraph,
    option: LayoutOption = 'horizontal',
    currentNodes?: Node<NodeData>[],
) => {
    const sortedGraph = graph;
    const isHorizontal = option === 'horizontal';
    const currentNodeMap = currentNodes ? new Map(currentNodes.map((node) => [node.id, node])) : new Map();

    const groupNodes = sortedGraph.nodes.filter((node) => node.type === 'policyGroup');
    const groupNodeIds = new Set(groupNodes.map((node) => node.id));
    const nodesWithoutGroups = sortedGraph.nodes.filter((node) => node.type !== 'policyGroup');

    const children = nodesWithoutGroups.map<ElkNode>((node) => {
        const currentNode = currentNodeMap.get(node.id);
        const width = currentNode?.width || node.width || 300;
        const height = currentNode?.height || node.height || 200;

        return {
            ...node,
            targetPosition: isHorizontal ? 'left' : 'top',
            sourcePosition: isHorizontal ? 'right' : 'bottom',
            width,
            height,
        };
    });

    const edgesWithoutGroups = sortedGraph.edges.filter(
        (edge) => !groupNodeIds.has(edge.source) && !groupNodeIds.has(edge.target),
    );
    const edges = edgesWithoutGroups as unknown as ElkExtendedEdge[];

    const layoutOptions = { ...ELK_DEFAULT_CONFIG, ...ELKS_CONFIGS[option] };
    const config: ElkNode = { id: 'root', layoutOptions, children, edges };

    try {
        const { children: layoutedChildren = [], edges: layoutedEdges = [] } = await elk.layout(config);
        const layoutedNodes = layoutedChildren.map(({ x = 0, y = 0, ...rest }) => ({
            ...rest,
            position: { x, y },
        }));
        const _nodes = layoutedNodes as unknown as Node<NodeData>[];

        groupNodes.forEach((groupNode) => {
            const childNodes = _nodes.filter((node) => node.parentId === groupNode.id);

            if (childNodes.length === 0) {
                return;
            }

            const childPositions = childNodes.map((child) => {
                const width = child.width || 300;
                const height = child.height || 200;
                return {
                    minX: child.position.x,
                    minY: child.position.y,
                    maxX: child.position.x + width,
                    maxY: child.position.y + height,
                };
            });

            const minX = Math.min(...childPositions.map((pos) => pos.minX));
            const minY = Math.min(...childPositions.map((pos) => pos.minY));
            const maxX = Math.max(...childPositions.map((pos) => pos.maxX));
            const maxY = Math.max(...childPositions.map((pos) => pos.maxY));

            const groupPosition = { x: minX, y: minY };
            const groupWidth = maxX - minX + 200;
            const groupHeight = maxY - minY + 200;

            groupNode.position = groupPosition;
            if (groupNode.data.style) {
                groupNode.data.style.width = groupWidth;
                groupNode.data.style.height = groupHeight;
            }
            if (!groupNode.width) {
                groupNode.width = groupWidth;
            }
            if (!groupNode.height) {
                groupNode.height = groupHeight;
            }

            childNodes.forEach((child) => {
                child.position = {
                    x: child.position.x - groupPosition.x,
                    y: child.position.y - groupPosition.y,
                };
            });
        });

        const allNodes = [..._nodes, ...groupNodes];
        const allEdges = [
            ...(layoutedEdges as unknown as Edge<EdgeData>[]),
            ...sortedGraph.edges.filter((edge) => groupNodeIds.has(edge.source) || groupNodeIds.has(edge.target)),
        ];

        return { ...graph, edges: allEdges, nodes: allNodes };
    } catch (error: unknown) {
        console.error('Failed to re-layout graph', error);
        throw error;
    }
};

export default layoutGraph;
